import React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Sheet from "@mui/joy/Sheet";
import DebounceInput from "../../../components/debounceInput/DebounceInput";
import SearchIcon from "@mui/icons-material/Search";
import IconButton, {iconButtonClasses} from "@mui/joy/IconButton";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import CircularProgress from "@mui/joy/CircularProgress";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import {NotificationService} from "../../../common/notification";
import {useNavigate} from "react-router-dom";
import Button from "@mui/joy/Button";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import {getPages} from "../../../common/pages/pages";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {AdminUserStatisticInfo} from "../../../models/user";
import {usersStatisticsApi} from "../usersApi";
import Select from "@mui/joy/Select";
import SortIcon from '@mui/icons-material/Sort';
import Option from "@mui/joy/Option";
import {formatRubles} from "../../../common/money";

export function UserStatisticTable() {
    const navigate = useNavigate();
    const [users, setUsers] = React.useState<AdminUserStatisticInfo[]>([]);
    const [orderBy, setOrderBy] = React.useState(6);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(1);
    const [filterSearch, setFilterSearch] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        loadData().catch(console.error);
    }, [page, filterSearch, orderBy])

    const loadData = async () => {
        setLoading(true)
        try {
            let result = await usersStatisticsApi(filterSearch, orderBy, page, 50);
            if (result.result) {
                const res = result.value!;
                setPageCount(Math.ceil(res.total / 50));
                setUsers(res.items)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <React.Fragment>
            <Sheet
                className="SearchAndFilters-mobile"
                sx={{display: {xs: 'flex', sm: 'none'}, my: 1, gap: 1}}
            >
                <DebounceInput
                    size="sm"
                    placeholder="Steam ID"
                    startDecorator={<SearchIcon/>}
                    sx={{flexGrow: 1}}
                    debounceTimeout={1000}
                    handleDebounce={(val) => setFilterSearch(val)}
                />
                <FormControl sx={{flex: 1}} size="sm">
                    <FormLabel>Сортировать по:</FormLabel>
                    <Select
                        size="sm"
                        startDecorator={<SortIcon/>}
                        value={orderBy}
                        onChange={(_, value) => setOrderBy(value ?? 6)}
                    >
                        <Option value={2}>Успешным заказам</Option>
                        <Option value={3}>Неуспешным заказам</Option>
                        <Option value={4}>Сумме покупок</Option>
                        <Option value={5}>Сумме продаж</Option>
                        <Option value={6}>Сумме пополнений</Option>
                        <Option value={7}>Сумме выплат</Option>
                        <Option value={8}>Балансу</Option>
                    </Select>
                </FormControl>
            </Sheet>
            <Box
                className="SearchAndFilters-tabletUp"
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: {xs: 'none', sm: 'flex'},
                    flexWrap: 'wrap',
                    gap: 1.5,
                    '& > *': {
                        minWidth: {xs: '120px', md: '160px'},
                    },
                }}
            >
                <FormControl sx={{flex: 1}} size="sm">
                    <FormLabel>Steam ID</FormLabel>
                    <DebounceInput
                        size="sm"
                        placeholder="Поиск"
                        startDecorator={<SearchIcon/>}
                        debounceTimeout={1000}
                        handleDebounce={(val) => setFilterSearch(val)}
                    />
                </FormControl>
                <FormControl sx={{flex: 1}} size="sm">
                    <FormLabel>Сортировать по:</FormLabel>
                    <Select
                        size="sm"
                        startDecorator={<SortIcon/>}
                        value={orderBy}
                        onChange={(_, value) => setOrderBy(value ?? 6)}
                    >
                        <Option value={2}>Успешным заказам</Option>
                        <Option value={3}>Неуспешным заказам</Option>
                        <Option value={4}>Сумме покупок</Option>
                        <Option value={5}>Сумме продаж</Option>
                        <Option value={6}>Сумме пополнений</Option>
                        <Option value={7}>Сумме выплат</Option>
                        <Option value={8}>Балансу</Option>
                    </Select>
                </FormControl>
            </Box>
            {loading ?
                <CircularProgress/> :
                <>
                    <Sheet
                        className="OrderTableContainer"
                        variant="outlined"
                        sx={{
                            width: '100%',
                            borderRadius: 'sm',
                            flexShrink: 1,
                            overflow: 'auto',
                            minHeight: 0,
                        }}
                    >
                        <Table
                            aria-labelledby="tableTitle"
                            stickyHeader
                            hoverRow
                            sx={{
                                display: {xs: 'none', sm: 'table'},
                                '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                '--Table-headerUnderlineThickness': '1px',
                                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                '--TableCell-paddingY': '4px',
                                '--TableCell-paddingX': '8px',
                            }}
                        >
                            <thead>
                            <tr>
                                <th style={{width: 140, padding: '12px 6px'}}>Steam Id</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Сумма покупок</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Суммы продаж</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Сумма пополнений</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Сумма выплат</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Скорость доставки (сек)</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Успешных заказов</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Неудачных заказов</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Баланс</th>
                            </tr>
                            </thead>
                            <tbody>
                            {users.map((row) => {
                                return (
                                    <tr key={row.id.toString()}>
                                        <td>
                                            <Link
                                                onClick={() => navigate(row.id.toString())}
                                                underline="hover"
                                                level="body-xs"
                                            >
                                                {row.id.toString()}
                                            </Link>
                                        </td>
                                        <td>
                                            <Typography
                                                level="body-xs">{formatRubles(row.buyingAmount)}</Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                level="body-xs">{formatRubles(row.salesAmount)}</Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                level="body-xs">{formatRubles(row.depositAmount)}</Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                level="body-xs">{formatRubles(row.withdrawalAmount)}</Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                level="body-xs">{new Intl.NumberFormat().format(row.deliverySpeed ?? 0)}</Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                level="body-xs">{new Intl.NumberFormat().format(row.totalSuccess)}</Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                level="body-xs">{new Intl.NumberFormat().format(row.totalFailures)}</Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                level="body-xs">{formatRubles(row.rub)}</Typography>
                                        </td>
                                    </tr>)
                            })}
                            </tbody>
                        </Table>
                    </Sheet>
                    <Box
                        className="Pagination-laptopUp"
                        sx={{
                            pt: 2,
                            gap: 1,
                            [`& .${iconButtonClasses.root}`]: {borderRadius: '50%'},
                            display: {
                                xs: 'none',
                                md: 'flex',
                            },
                        }}
                    >
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            startDecorator={<KeyboardArrowLeftIcon/>}
                            disabled={page === 1}
                            onClick={() => setPage(state => state - 1)}
                        >
                            Назад
                        </Button>

                        <Box sx={{flex: 1}}/>
                        {getPages(pageCount).map((p) => (
                            <IconButton
                                key={p}
                                size="sm"
                                variant={Number(p) ? 'outlined' : 'plain'}
                                color={page === Number(p) ? "primary" : "neutral"}
                                onClick={() => setPage(Number(p))}
                            >
                                {p}
                            </IconButton>
                        ))}
                        <Box sx={{flex: 1}}/>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            endDecorator={<KeyboardArrowRightIcon/>}
                            disabled={page === pageCount}
                            onClick={() => setPage(state => state + 1)}
                        >
                            Дальше
                        </Button>
                    </Box>

                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        {users.map((listItem) => {
                            return (
                                <List key={listItem.id.toString()} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                    <ListItem
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'start',
                                        }}
                                    >
                                        <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                            <div>
                                                <Link
                                                    onClick={() => navigate(listItem.id.toString())}
                                                    underline="hover"
                                                    level="body-xs"
                                                >
                                                    {listItem.id.toString()}
                                                </Link>
                                                <Typography
                                                    level="body-xs">Сумма
                                                    покупок: {formatRubles(listItem.buyingAmount)}</Typography>
                                                <Typography
                                                    level="body-xs">Суммы
                                                    продаж: {formatRubles(listItem.salesAmount)}</Typography>
                                                <Typography
                                                    level="body-xs">Сумма
                                                    пополнений: {formatRubles(listItem.depositAmount)}</Typography>
                                                <Typography
                                                    level="body-xs">Сумма
                                                    выплат: {formatRubles(listItem.withdrawalAmount)}</Typography>
                                                <Typography
                                                    level="body-xs">Скорость доставки,
                                                    сек. {new Intl.NumberFormat().format(listItem.deliverySpeed ?? 0)}</Typography>
                                                <Typography
                                                    level="body-xs">Успешных
                                                    заказов: {new Intl.NumberFormat().format(listItem.totalSuccess)}</Typography>
                                                <Typography
                                                    level="body-xs">Неудачных
                                                    заказов: {new Intl.NumberFormat().format(listItem.totalFailures)}</Typography>
                                                <Typography
                                                    level="body-xs">Баланс: {formatRubles(listItem.rub)}</Typography>
                                            </div>
                                        </ListItemContent>
                                    </ListItem>
                                    <ListDivider/>
                                </List>)
                        })}
                        <Box
                            className="Pagination-mobile"
                            sx={{display: {xs: 'flex', md: 'none'}, alignItems: 'center', py: 2}}
                        >
                            <IconButton
                                aria-label="previous page"
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                disabled={page === 1}
                                onClick={() => setPage(state => state - 1)}
                            >
                                <KeyboardArrowLeftIcon/>
                            </IconButton>
                            <Typography level="body-sm" sx={{mx: 'auto'}}>
                                Страница {page} из {pageCount}
                            </Typography>
                            <IconButton
                                aria-label="next page"
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                disabled={page === pageCount}
                                onClick={() => setPage(state => state + 1)}
                            >
                                <KeyboardArrowRightIcon/>
                            </IconButton>
                        </Box>
                    </Box>
                </>
            }
        </React.Fragment>
    );
}
